import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, FluentProvider, Option, Select, Spinner, Tooltip } from '@fluentui/react-components';
import { ArrowSync16Regular, DocumentBulletList16Regular, Lightbulb16Regular } from '@fluentui/react-icons';
import type { FormResponses, Issue, ProjectContext } from '@qcbot/shared';
import { qcbotLightTheme } from './theme';
import { ProjectContextForm, emptyContext } from './components/ProjectContextForm';
import { TrinityLogo } from './components/TrinityLogo';
import { RunProgress } from './components/RunProgress';
import { IssueList } from './components/IssueList';
import { ChatPanel } from './components/ChatPanel';
import { CollabBar, type IssueTab } from './components/CollabBar';
import { JoinBanner } from './components/JoinBanner';
import { ShareDialog } from './components/ShareDialog';
import { ProjectTemplateForm, type ProjectTemplateSelection } from './components/ProjectTemplateForm';
import { TemplateRulesDialog } from './components/TemplateRulesDialog';
import { SuggestRuleDialog } from './components/SuggestRuleDialog';
import { getActiveFileBytes, getCurrentFileName, getDocumentType, getSourceDocumentKey } from './officeApi';
import { ensureDocumentId } from './documentIdentity';
import { useRunCollab } from './useRunCollab';
import {
  ApiError,
  cancelReview,
  createReview,
  downloadAnnotatedDeck,
  fetchIssues,
  fetchQcTemplate,
  fetchQcTemplateForm,
  fetchResolvedRules,
  fetchStatus,
  isTerminalPhase,
  retryablePasses,
  retryReview,
  subscribeToProgress,
  type ResolvedRuleSummary,
  type ReviewProgress,
} from './api';
import { ensureMsalInitialized, getActiveAccount, signIn, signOut } from './authConfig';
import { clearRunState, loadRunState, loadSelection, saveRunState, saveSelection } from './persistedRun';

type Stage = 'project-template' | 'setup' | 'running' | 'results';

const initialProgress: ReviewProgress = { phase: 'queued', slidesProcessed: 0, totalSlides: 0, issuesFoundSoFar: 0 };

// If a run shows no progress for this long, offer a way out instead of leaving the user staring
// at a frozen progress bar forever — covers a run the server lost track of (e.g. it crashed
// after the backend's own startup cleanup already ran, or the SSE stream silently stopped
// reconnecting) that never got marked 'failed'.
const STALE_RUN_MS = 2 * 60 * 1000;

function runActionMessage(error: unknown, verb: 'stop' | 'retry'): string {
  if (error instanceof ApiError && error.status === 403) return `Only the person who started this run can ${verb} it.`;
  return (error as Error).message;
}

export function App() {
  const [authChecked, setAuthChecked] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  const [stage, setStage] = useState<Stage>('project-template');
  const [selection, setSelection] = useState<ProjectTemplateSelection>(() => loadSelection() ?? {});
  const [submitting, setSubmitting] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ReviewProgress>(initialProgress);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [resolvedRules, setResolvedRules] = useState<ResolvedRuleSummary[]>([]);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [looksStuck, setLooksStuck] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(new Set());
  const [hideAiSuggestions, setHideAiSuggestions] = useState(false);
  const [templateAiSuggestionsEnabled, setTemplateAiSuggestionsEnabled] = useState(false);
  const [issueTab, setIssueTab] = useState<IssueTab>('open');
  const [shareOpen, setShareOpen] = useState(false);
  // True from the moment Stop/Retry is clicked until the run's phase changes, so they can't be double-clicked.
  const [runActionBusy, setRunActionBusy] = useState(false);
  const [runActionError, setRunActionError] = useState<string | null>(null);
  const unsubscribeRef = useRef<() => void>();
  const lastProgressAtRef = useRef(Date.now());

  const collabState = useRunCollab({ runId, enabled: stage === 'results', setIssues, onRemoved: handleRemoved });

  useEffect(() => () => unsubscribeRef.current?.(), []);

  // Drives the "Hide AI suggestions" control's visibility (see below) — the template's own
  // setting, not whether any ai_suggested issue has actually turned up yet in this run, so it
  // appears/disappears consistently the moment a template is picked, and also survives a
  // taskpane remount into an already-running/finished run (loadSelection() only persists
  // projectId/qcTemplateId, not this flag).
  useEffect(() => {
    if (!selection.qcTemplateId) {
      setTemplateAiSuggestionsEnabled(false);
      return;
    }
    let cancelled = false;
    void fetchQcTemplate(selection.qcTemplateId).then((template) => {
      if (!cancelled) setTemplateAiSuggestionsEnabled(template?.aiSuggestionsEnabled ?? false);
    });
    return () => {
      cancelled = true;
    };
  }, [selection.qcTemplateId]);

  // Resolve any existing MSAL session on mount (e.g. the taskpane iframe remounted but the
  // browser-level MSAL cache survived) before deciding whether to show the sign-in screen.
  useEffect(() => {
    void ensureMsalInitialized().then(() => {
      setSignedIn(Boolean(getActiveAccount()));
      setAuthChecked(true);
    });
  }, []);

  async function handleSignIn() {
    setSigningIn(true);
    setSignInError(null);
    try {
      await signIn();
      setSignedIn(true);
    } catch (e) {
      setSignInError((e as Error).message);
    } finally {
      setSigningIn(false);
    }
  }

  // Poll (rather than reset-on-every-progress-tick) so a run that stops ticking mid-'running'
  // gets flagged even though nothing ever calls setProgress again.
  useEffect(() => {
    if (stage !== 'running') {
      setLooksStuck(false);
      return;
    }
    const interval = setInterval(() => {
      setLooksStuck(Date.now() - lastProgressAtRef.current > STALE_RUN_MS);
    }, 5000);
    return () => clearInterval(interval);
  }, [stage]);

  // Each stage renders as a full replacement, not an append — but without this, the pane's
  // scroll position carries over from whatever was scrolled to on the previous stage. A tall
  // team/template screen (e.g. one with several template-specific fields loaded) left scrolled
  // down would then show the *next* stage already scrolled past its top, looking like the new
  // content got appended under the old rather than replacing it. Reset on every stage change so
  // each stage consistently starts at its own top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stage]);

  // Rehydrate on mount: the Office taskpane iframe remounts from scratch whenever the user
  // switches to another pane (e.g. native Comments) and back, wiping in-memory React state —
  // so if a run was in progress or already finished, restore it instead of showing the form.
  useEffect(() => {
    const saved = loadRunState();
    if (!saved) return;
    // Run no longer exists or isn't accessible any more (e.g. backend restarted, or removed from it) — fall back to the form.
    openExistingRun(saved.runId).catch(() => clearRunState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Loads a run's current state and lands on the matching stage; shared by remount and Join. */
  async function openExistingRun(id: string) {
    const status = await fetchStatus(id);
    setRunId(id);
    setProgress(status);
    lastProgressAtRef.current = Date.now();
    void fetchResolvedRules(id).then(setResolvedRules);
    if (isTerminalPhase(status.phase)) {
      setIssues(await fetchIssues(id));
      setStage('results');
      saveRunState(id, 'results');
    } else {
      setStage('running');
      saveRunState(id, 'running');
      attachToRun(id, status.issuesFoundSoFar);
    }
  }

  async function handleJoin(id: string) {
    setStartError(null);
    try {
      await ensureDocumentId();
      await openExistingRun(id);
    } catch (e) {
      setStartError((e as Error).message);
    }
  }

  async function handleCancel() {
    if (!runId) return;
    setRunActionBusy(true);
    setRunActionError(null);
    try {
      // Nothing more to do on success: the run's 'cancelled' phase arrives on the progress stream.
      await cancelReview(runId);
    } catch (e) {
      setRunActionBusy(false);
      setRunActionError(runActionMessage(e, 'stop'));
      // 409: the run finished (or was stopped) in the meantime — resync with what the server says.
      if (e instanceof ApiError && e.status === 409) void openExistingRun(runId).catch(() => undefined);
    }
  }

  async function handleRetry() {
    if (!runId) return;
    setRunActionBusy(true);
    setRunActionError(null);
    try {
      await retryReview(runId);
      // The run is processing again: reattach to its live progress.
      await openExistingRun(runId);
    } catch (e) {
      setRunActionError(runActionMessage(e, 'retry'));
    } finally {
      setRunActionBusy(false);
    }
  }

  /** The run's owner removed this user: drop the saved run and go back to the start. */
  function handleRemoved() {
    handleConfirmReset();
    setStartError('You were removed from that run.');
  }

  function attachToRun(id: string, startingIssueCount = 0) {
    unsubscribeRef.current?.();
    // Local counter (not React state) so each SSE tick compares against the count as of the
    // last fetch, rather than a stale `issues` closure captured when this callback was created.
    let lastFetchedCount = startingIssueCount;
    // Snapshotted once at run start on the backend (see reviews/runner.ts) — fetched once here
    // too, rather than re-fetched on every SSE tick, since it never changes mid-run.
    void fetchResolvedRules(id).then(setResolvedRules);
    unsubscribeRef.current = subscribeToProgress(id, async (p) => {
      setProgress(p);
      lastProgressAtRef.current = Date.now();
      if (p.issuesFoundSoFar > lastFetchedCount) {
        lastFetchedCount = p.issuesFoundSoFar;
        setIssues(await fetchIssues(id));
      }
      if (isTerminalPhase(p.phase)) {
        setRunActionBusy(false);
        setIssues(await fetchIssues(id));
        setStage('results');
        saveRunState(id, 'results');
        unsubscribeRef.current?.();
      }
    });
  }

  async function handleStart(context: ProjectContext, formResponses: FormResponses, chosen: ProjectTemplateSelection = selection) {
    setSubmitting(true);
    setStartError(null);
    try {
      // Before saving any run state: binds the saved run to this document, even an unsaved one.
      await ensureDocumentId();
      const fileName = getCurrentFileName();
      const bytes = await getActiveFileBytes();
      const id = await createReview(bytes, fileName, context, { ...chosen, formResponses, documentKey: await getSourceDocumentKey() });
      setRunId(id);
      setIssues([]);
      setStage('running');
      saveRunState(id, 'running');
      attachToRun(id);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'project_unavailable') {
        // The project/template was archived while the taskpane was open — back to project selection.
        setSelection({});
        setStage('project-template');
      }
      setStartError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  /** After project/template selection: a template with form fields goes to the form step; one with
   * none (or no template at all) has nothing to ask, so the run starts straight away. */
  async function handleSelectionContinue(chosen: ProjectTemplateSelection) {
    setStartError(null);
    setSelection(chosen);
    saveSelection({ projectId: chosen.projectId, qcTemplateId: chosen.qcTemplateId });
    setSubmitting(true);
    let needsForm = true;
    try {
      needsForm = chosen.qcTemplateId ? (await fetchQcTemplateForm(chosen.qcTemplateId)).fields.length > 0 : false;
    } catch {
      // Can't tell — show the form step, which reports its own load problems.
    }
    if (needsForm) {
      setSubmitting(false);
      setStage('setup');
      return;
    }
    await handleStart(emptyContext, {}, chosen);
  }

  function handleConfirmReset() {
    unsubscribeRef.current?.();
    clearRunState();
    setConfirmingReset(false);
    setLooksStuck(false);
    setRunId(null);
    setIssues([]);
    setResolvedRules([]);
    setSelectedRuleIds(new Set());
    setHideAiSuggestions(false);
    setIssueTab('open');
    setShareOpen(false);
    setRunActionBusy(false);
    setRunActionError(null);
    setProgress(initialProgress);
    setSelection({});
    setStage('project-template');
  }

  const rulesByControlId = useMemo(() => new Map(resolvedRules.map((r) => [r.controlId, r])), [resolvedRules]);

  // Options reflect rules actually present among this run's issues, not every rule the template
  // defines — no point offering a filter option with zero matching issues.
  const availableRuleOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const issue of issues) {
      if (!issue.controlId || seen.has(issue.controlId)) continue;
      seen.set(issue.controlId, rulesByControlId.get(issue.controlId)?.title ?? issue.controlId);
    }
    return [...seen.entries()];
  }, [issues, rulesByControlId]);

  const filteredIssues = useMemo(
    () =>
      issues.filter((issue) => {
        if (hideAiSuggestions && issue.origin === 'ai_suggested') return false;
        if (selectedRuleIds.size > 0 && !(issue.controlId && selectedRuleIds.has(issue.controlId))) return false;
        if (stage === 'results' && (issue.status === 'Fixed') !== (issueTab === 'fixed')) return false;
        return true;
      }),
    [issues, selectedRuleIds, hideAiSuggestions, stage, issueTab],
  );
  const fixedCount = useMemo(() => issues.filter((i) => i.status === 'Fixed').length, [issues]);

  if (!authChecked) {
    return (
      <FluentProvider theme={qcbotLightTheme}>
        <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size="tiny" /> Loading…
        </div>
      </FluentProvider>
    );
  }

  if (!signedIn) {
    return (
      <FluentProvider theme={qcbotLightTheme}>
        <div style={{ padding: 16, fontFamily: 'Segoe UI, sans-serif', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <TrinityLogo height={32} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 13, color: '#666' }}>Sign in with your Microsoft account to start a QC review.</p>
          {signInError && (
            <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>
              {signInError}
            </div>
          )}
          <Button appearance="primary" onClick={handleSignIn} disabled={signingIn}>
            {signingIn ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </FluentProvider>
    );
  }

  return (
    <FluentProvider theme={qcbotLightTheme}>
      <div style={{ padding: 16, fontFamily: 'Segoe UI, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
          <TrinityLogo height={32} />
          <Button appearance="transparent" size="small" style={{ fontSize: 12, minWidth: 0, padding: 0 }} onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>

        {startError && (stage === 'project-template' || stage === 'setup') && (
          <div role="alert" style={{ color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>
            {startError}
          </div>
        )}

        {stage === 'project-template' && (
          <>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 12px' }}>
              No QC results for this {getDocumentType() === 'docx' ? 'document' : 'presentation'} yet. Choose a project and QC template to run QC.
            </p>
            <JoinBanner onJoin={(id) => void handleJoin(id)} />
            <ProjectTemplateForm
              initialSelection={selection}
              busy={submitting}
              onContinue={(s) => void handleSelectionContinue(s)}
            />
          </>
        )}

        {stage === 'setup' && (
          <ProjectContextForm
            onSubmit={handleStart}
            onBack={() => setStage('project-template')}
            submitting={submitting}
            qcTemplateId={selection.qcTemplateId}
            initialFormResponses={selection.formResponses}
          />
        )}

        {stage === 'running' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <RunProgress progress={progress} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button appearance="secondary" disabled={runActionBusy} onClick={() => void handleCancel()} style={{ alignSelf: 'flex-start' }}>
                {runActionBusy ? 'Stopping…' : 'Stop QC'}
              </Button>
              {runActionError && (
                <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>
                  {runActionError}
                </div>
              )}
            </div>
            {looksStuck && (
              <div style={{ fontSize: 13, color: '#b91c1c', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>This run hasn&apos;t made progress in a while and may have stopped on the server.</div>
                <Button appearance="secondary" onClick={() => setConfirmingReset(true)} style={{ alignSelf: 'flex-start' }}>
                  Cancel and start new QC
                </Button>
              </div>
            )}
            <IssueList issues={issues} runId={runId} rulesByControlId={rulesByControlId} unitLabel={progress.unitLabel} unitAnchors={progress.unitAnchors} />
          </div>
        )}

        {stage === 'results' && runId && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(progress.error || progress.phase === 'cancelled') && (
              <div style={{ color: progress.phase === 'cancelled' ? '#666' : '#b91c1c', fontSize: 13 }}>
                {progress.phase === 'cancelled'
                  ? `QC was stopped. Showing the ${issues.length} issue(s) found before it stopped.`
                  : progress.phase === 'failed'
                    ? `Run stopped early: ${progress.error}. Showing the ${issues.length} issue(s) found before it stopped.`
                    : progress.error}
              </div>
            )}
            {retryablePasses(progress).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button appearance="primary" disabled={runActionBusy} icon={<ArrowSync16Regular />} onClick={() => void handleRetry()} style={{ alignSelf: 'flex-start' }}>
                  {runActionBusy ? 'Resuming…' : progress.phase === 'done' ? 'Retry failed steps' : 'Resume QC'}
                </Button>
                <div style={{ fontSize: 12, color: '#666' }}>Only the steps that didn&apos;t finish are re-run; issues already found are kept.</div>
                {runActionError && (
                  <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>
                    {runActionError}
                  </div>
                )}
              </div>
            )}
            <ChatPanel runId={runId} issueCount={issues.length} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button appearance="primary" onClick={() => void downloadAnnotatedDeck(runId, getCurrentFileName())}>
                {progress.documentType === 'docx' ? 'Download annotated document' : 'Download annotated deck'}
              </Button>
              <div style={{ display: 'flex', gap: 8 }}>
                <Tooltip content="Run new QC" relationship="label">
                  <Button style={{ flex: 1, minWidth: 0 }} size="small" icon={<ArrowSync16Regular />} appearance="secondary" onClick={() => setConfirmingReset(true)} />
                </Tooltip>
                {selection.qcTemplateId && (
                  <Tooltip content="Template QC rules" relationship="label">
                    <Button style={{ flex: 1, minWidth: 0 }} size="small" icon={<DocumentBulletList16Regular />} appearance="outline" onClick={() => setRulesDialogOpen(true)} />
                  </Tooltip>
                )}
                {selection.qcTemplateId && (
                  <Tooltip content="Suggest new rule" relationship="label">
                    <Button style={{ flex: 1, minWidth: 0 }} size="small" icon={<Lightbulb16Regular />} appearance="outline" onClick={() => setSuggestDialogOpen(true)} />
                  </Tooltip>
                )}
              </div>
            </div>
            <CollabBar
              openCount={issues.length - fixedCount}
              fixedCount={fixedCount}
              tab={issueTab}
              onTab={setIssueTab}
              state={collabState}
              onShare={() => setShareOpen(true)}
            />
            {(availableRuleOptions.length > 0 || templateAiSuggestionsEnabled) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12 }}>
                {availableRuleOptions.length > 0 && (
                  <Dropdown
                    multiselect
                    placeholder="Filter by rule"
                    style={{ width: '100%' }}
                    selectedOptions={[...selectedRuleIds]}
                    value={[...selectedRuleIds]
                      .map((id) => availableRuleOptions.find(([controlId]) => controlId === id)?.[1] ?? id)
                      .join(', ')}
                    onOptionSelect={(_, data) => setSelectedRuleIds(new Set(data.selectedOptions))}
                  >
                    {availableRuleOptions.map(([controlId, title]) => (
                      <Option key={controlId} value={controlId} text={title}>
                        {title}
                      </Option>
                    ))}
                  </Dropdown>
                )}
                {templateAiSuggestionsEnabled && (
                  <Checkbox
                    label="Hide AI suggestions"
                    checked={hideAiSuggestions}
                    onChange={(_, d) => setHideAiSuggestions(Boolean(d.checked))}
                  />
                )}
              </div>
            )}
            <IssueList
              issues={filteredIssues}
              runId={runId}
              rulesByControlId={rulesByControlId}
              unitLabel={progress.unitLabel}
              unitAnchors={progress.unitAnchors}
              collab={collabState.collab}
              emptyMessage={issues.length === 0 ? 'No issues found.' : issueTab === 'open' ? 'No open issues.' : 'Nothing fixed yet.'}
            />
            <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} state={collabState} meId={collabState.collab.me} />
            {progress.warning && (
              <details style={{ fontSize: 12, color: '#666' }}>
                <summary style={{ cursor: 'pointer' }}>Note: some slides were skipped when adding comments</summary>
                <div style={{ marginTop: 4 }}>{progress.warning}</div>
              </details>
            )}
          </div>
        )}

        {selection.qcTemplateId && (
          <>
            <TemplateRulesDialog open={rulesDialogOpen} onClose={() => setRulesDialogOpen(false)} templateId={selection.qcTemplateId} />
            <SuggestRuleDialog open={suggestDialogOpen} onClose={() => setSuggestDialogOpen(false)} templateId={selection.qcTemplateId} />
          </>
        )}

        <Dialog open={confirmingReset} onOpenChange={(_, data) => setConfirmingReset(data.open)}>
          <DialogSurface style={{ margin: 16 }}>
            <DialogBody>
              <DialogTitle>Start a new QC run?</DialogTitle>
              <DialogContent>
                This clears the current results view and shows the setup form again. The completed run itself is not deleted.
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setConfirmingReset(false)}>
                  Cancel
                </Button>
                <Button appearance="primary" onClick={handleConfirmReset}>
                  Yes, start new QC
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
    </FluentProvider>
  );
}
